import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { useLocation } from 'react-router-dom';

import NovusLogo from '@assets/icons/novusLogo.svg?component';
import OrderConfirmationIcon from '@assets/icons/orderConfirmation.svg?component';
import { CPQ_ORDER_DATA_KEY } from '@t/session.types';
import { downloadOrderSummary } from '@services/cpqService';
import { closeCpqApp } from '@utils/sessionUtils';
import { useCustomer } from '@contexts/CustomerContext';

interface OrderData {
  shoppingCartId?: string;
  orderNumber?: string;
  orderId?: string;
  sessionId?: string;
}

interface OrderConfirmationPageProps {
  /** Provided when embedded in a host app — "Go to Homepage" closes the
   *  embedded flow (returning the host to its own page) instead of navigating
   *  the browser to the public Novus site. */
  onClose?: () => void;
}

const getOrderData = (): OrderData => {
  try {
    const raw = localStorage.getItem(CPQ_ORDER_DATA_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const OrderConfirmationPage = ({ onClose }: OrderConfirmationPageProps) => {
  const location = useLocation();
  const { branding } = useCustomer();
  const stateData = (location.state as OrderData | null) ?? {};
  const orderData = stateData.shoppingCartId ? stateData : getOrderData();
  const { shoppingCartId, sessionId } = orderData;

  const [isDownloading, setIsDownloading] = useState(false);

  const clearOrderState = () => {
    localStorage.removeItem(CPQ_ORDER_DATA_KEY);
  };

  const handleGoHome = () => {
    if (onClose) {
      closeCpqApp(onClose, branding.homepageUrl);
      return;
    }
    clearOrderState();
    window.open(branding.homepageUrl, '_blank');
  };

  const handleDownloadSummary = async () => {
    if (!sessionId) return;
    setIsDownloading(true);
    try {
      const blob = await downloadOrderSummary(sessionId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'order-summary.pdf';
      link.click();
      URL.revokeObjectURL(url);
      clearOrderState();
    } catch {
      // Fallback to print if PDF download fails
      window.print();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Paper variant="orderConfirmationPage" elevation={0}>
      {/* Header */}
      <Paper variant="orderConfirmationHeader" elevation={0} component="header">
        <Stack className="OrderConfirmationHeader-row">
          <IconButton onClick={handleGoHome}>
            <SvgIcon component={NovusLogo} inheritViewBox className="OrderConfirmationHeader-logo" />
          </IconButton>
        </Stack>
      </Paper>

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: { xs: '40px 16px', sm: '60px 30px' },
        }}
      >
        <Paper
          variant="orderConfirmationCard"
          elevation={0}
          sx={{ padding: { xs: '48px 24px', sm: '60px 80px' } }}
        >
          <SvgIcon component={OrderConfirmationIcon} inheritViewBox className="OrderConfirmationCard-icon" />

          <Typography className="OrderConfirmationHeading">Thank You For Your Order!</Typography>

          <Typography className="OrderConfirmationBody">
            {`Great news, your order has been submitted. You can track its progress and view updates anytime in ${branding.portalName}. Thank you for choosing us.`}
          </Typography>

          {shoppingCartId && (
            <Typography className="OrderConfirmationNumber">
              <strong>Confirmation Number</strong>: {shoppingCartId}
            </Typography>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap="16px"
            sx={{ mt: '16px', '@media print': { display: 'none' } }}
          >
            <Button variant="contained" onClick={handleGoHome}>
              Go to Homepage
            </Button>

            <Button
              variant="contained"
              onClick={handleDownloadSummary}
              disabled={isDownloading || !sessionId}
              startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isDownloading ? 'DOWNLOADING...' : 'DOWNLOAD ORDER CONFIRMATION'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Paper>
  );
};

export default OrderConfirmationPage;
